import React, { memo } from "react";

const Card = memo(({ title, value, icon, text, bgColor, onClick, titleLoading, isAgedCare }) => {
  const isIncident = title === "Incidents";
  const cardBgColor = bgColor || "#FFFFFF";
  const isParticipantCard =
    title === "Active Participants" ||
    title === "Active Consumers" ||
    titleLoading;
  const isActiveStaff = title === "Active Staff " || title === "Active Staff";
  
  // Determine card color theme based on title
  const getCardTheme = () => {
    if (isIncident) return { bg: 'bg-gradient-to-br from-red-500 to-rose-600', text: 'text-white', iconBg: 'bg-white/20' };
    // Aged Care → blue gradient; NDIS → white
    if (isActiveStaff) {
      if (isAgedCare) {
        return { bg: 'bg-gradient-to-br from-blue-500 to-cyan-500', text: 'text-white', iconBg: 'bg-white/20' };
      }
      return { bg: 'bg-white', text: 'text-gray-800', iconBg: 'bg-gray-100' };
    }
    if (isParticipantCard) return { bg: 'bg-gradient-to-br from-indigo-500 to-purple-500', text: 'text-white', iconBg: 'bg-white/20' };
    if (title === "Feedback") return { bg: 'bg-gradient-to-br from-emerald-500 to-teal-500', text: 'text-white', iconBg: 'bg-white/20' };
    return { bg: 'bg-white', text: 'text-gray-800', iconBg: 'bg-gray-100' };
  };

  const theme = getCardTheme();
  
  return (
    <div
      onClick={onClick}
      className={`
        relative overflow-hidden
        ${theme.bg}
        border border-gray-200/50
        shadow-md hover:shadow-xl 
        rounded-2xl p-4 sm:p-5
        flex items-center justify-between
        transition-all duration-300 
        transform hover:-translate-y-0.5 hover:scale-[1.02]
        cursor-pointer
        group
        min-h-[100px] sm:min-h-[120px]
      `}
    >
      {/* Animated background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 right-0 w-32 h-32 bg-white rounded-full -mr-16 -mt-16 blur-2xl group-hover:blur-3xl transition-all" />
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-white rounded-full -ml-12 -mb-12 blur-xl group-hover:blur-2xl transition-all" />
      </div>
      
      {/* Content */}
      <div className="flex-1 relative z-10">
        <div className="flex items-center gap-3 mb-2">
          <div className={`
            p-2 sm:p-2.5 rounded-xl 
            transition-all duration-300
            ${theme.iconBg} group-hover:scale-110
            backdrop-blur-sm
          `}>
            <img 
              src={icon} 
              alt={title} 
              className="w-5 h-5 sm:w-6 sm:h-6 object-contain"
              style={{ filter: isIncident || theme.text === 'text-white' ? 'brightness(0) invert(1)' : 'none' }}
            />
          </div>
          <div className="flex-1 min-w-0">
            {titleLoading ? (
              <div className={`h-3.5 sm:h-4 w-24 sm:w-28 rounded animate-pulse ${theme.text === 'text-white' ? 'bg-white/30' : 'bg-gray-200'}`} />
            ) : (
              <p className={`
                text-xs sm:text-sm font-medium
                ${theme.text} opacity-90
                truncate
              `}>
                {title}
              </p>
            )}
            {text && (
              <span className={`
                text-[10px] sm:text-xs font-normal
                ${theme.text} opacity-70
              `}>
                {text}
              </span>
            )}
          </div>
        </div>
        <p className={`
          text-2xl sm:text-3xl font-bold
          ${theme.text}
          group-hover:scale-105 transition-transform
        `}>
          {value?.toLocaleString() || 0}
        </p>
      </div>
      
      {/* Decorative accent */}
      <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -mr-10 -mt-10 group-hover:scale-150 transition-transform duration-500" />
      
      {/* Hover effect indicator */}
      <div className={`
        absolute bottom-0 left-0 right-0 h-0.5 
        transform scale-x-0 group-hover:scale-x-100
        transition-transform duration-300
        ${theme.text === 'text-white' ? 'bg-white/50' : 'bg-gradient-to-r from-blue-500 to-cyan-500'}
      `} />
    </div>
  );
});

Card.displayName = 'Card';

export default Card;
